// =============================================================================
// VandieZ admin-web -- Commission rules (/commission/rules)
// =============================================================================
// CRUD over the `commission_rules` table.  Scope precedence on the
// backend resolver is load > user > vehicle_type > global, so admins
// stack overrides for individual customers / lanes on top of the
// global 2%+2% fallback.
//
// Soft-delete only: deactivation flips effective_to=now() so the row
// stops matching the resolver but stays on disk for audit.
// =============================================================================

import {
  AlertCircle,
  IndianRupee,
  Loader2,
  Plus,
  Trash2,
} from "lucide-react";
import type React from "react";
import { useCallback, useEffect, useState } from "react";

import { commissionAdminApi, masterdataAdminApi } from "@/api/client";
import { PageHeader } from "@/components/AdminLayout";
import { UserPicker } from "@/components/UserPicker";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/lib/auth";
import { cn } from "@/lib/utils";
import { ApiError } from "@/types/auth";
import type { AdminUserSummary } from "@/types/rbac";
import type {
  CommissionRateKind,
  CommissionRule,
  CommissionRuleCreatePayload,
  CommissionScopeType,
} from "@/types/commission";

// The eight seeded `pricing_models` codes. A closed list rather than a
// fetch: these are the platform's pricing MODES, not catalog data an
// operator adds, and a dropdown that could silently go empty on a failed
// request is worse than one that cannot.
const PRICING_MODEL_CODES = [
  "bidding", "custom", "fixed", "km", "time", "volume", "weight", "zone",
] as const;

const SCOPE_OPTIONS: { value: CommissionScopeType; label: string }[] = [
  { value: "global", label: "Global (platform default)" },
  { value: "user", label: "User (per-driver or per-shipper override)" },
  { value: "load", label: "Load (single-trip override)" },
  // AD-11 (p110). The backend resolver has supported route scope since
  // migration 0037 and this list never offered it, so an operator could
  // not create one -- the rule existed and was unreachable.
  { value: "route", label: "Route (a lane, e.g. chennai->coimbatore)" },
  // AD-11 (p110)'s fourth axis. A fixed-price load and a bidding load are
  // different businesses; charging the same rate on both was never a
  // decision, it just fell out of the schema.
  { value: "pricing_model", label: "Pricing model (e.g. fixed, bidding)" },
  { value: "vehicle_type", label: "Vehicle type (e.g. open_body)" },
];

const RATE_KIND_OPTIONS: { value: CommissionRateKind; label: string }[] = [
  { value: "percentage", label: "Percentage (% of trip value)" },
  { value: "flat", label: "Flat (fixed ₹ amount)" },
  { value: "hybrid", label: "Hybrid (percentage + flat)" },
];

const SCOPE_PILL: Record<CommissionScopeType, string> = {
  global: "bg-muted text-muted-foreground",
  user: "bg-primary-subtle text-primary-subtle-foreground",
  load: "bg-warning-subtle text-warning-subtle-foreground",
  route: "bg-info-subtle text-info-subtle-foreground",
  pricing_model: "bg-warning-subtle text-warning-subtle-foreground",
  vehicle_type: "bg-success-subtle text-success-subtle-foreground",
};

function formatDate(iso: string | null): string {
  if (iso === null) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

/// Decimal-as-string to "X.XX %" — 0.02 → "2.00 %", 0.04 → "4.00 %".
function formatRate(decimalStr: string): string {
  const n = Number.parseFloat(decimalStr);
  if (Number.isNaN(n)) return decimalStr;
  return `${(n * 100).toFixed(2)} %`;
}

/// Decimal-as-string INR amount to "₹200.00".
function formatInr(decimalStr: string): string {
  const n = Number.parseFloat(decimalStr);
  if (Number.isNaN(n)) return decimalStr;
  return `₹${n.toFixed(2)}`;
}

function shortId(id: string): string {
  return id.slice(0, 8);
}

interface CreateRuleFormProps {
  onCreated: () => void;
  onCancel: () => void;
}

function CreateRuleForm({ onCreated, onCancel }: CreateRuleFormProps): React.JSX.Element {
  const { accessToken } = useAuth();
  const [scopeType, setScopeType] = useState<CommissionScopeType>("global");
  const [scopeId, setScopeId] = useState<string>("");
  const [scopeUser, setScopeUser] = useState<AdminUserSummary | null>(null);
  const [scopeValue, setScopeValue] = useState<string>("");
  // AD-11: real lanes from the route master (0109), offered as datalist
  // suggestions so an operator picks one instead of guessing the
  // `origin->dest` format the resolver actually matches on.
  const [lanes, setLanes] = useState<string[]>([]);
  // Suggestions only. Failure is deliberately silent and the field stays
  // free text: lanes absent from the master are perfectly valid to price,
  // so a masters outage must not block a pricing change.
  useEffect(() => {
    if (accessToken === null) return;
    const controller = new AbortController();
    void masterdataAdminApi
      .listRoutes(accessToken, { active: true }, controller.signal)
      .then((rows) => {
        setLanes(
          rows
            .filter(
              (r) => r.origin_district !== null && r.dest_district !== null,
            )
            .map(
              (r) =>
                `${r.origin_district!.toLowerCase()}->` +
                `${r.dest_district!.toLowerCase()}`,
            ),
        );
      })
      .catch(() => {
        setLanes([]);
      });
    return () => {
      controller.abort();
    };
  }, [accessToken]);
  const [driverRate, setDriverRate] = useState<string>("0.02");
  const [shipperRate, setShipperRate] = useState<string>("0.02");
  const [rateKind, setRateKind] = useState<CommissionRateKind>("percentage");
  const [driverFlatInr, setDriverFlatInr] = useState<string>("");
  const [shipperFlatInr, setShipperFlatInr] = useState<string>("");
  const [notes, setNotes] = useState<string>("");
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const showRateInputs = rateKind === "percentage" || rateKind === "hybrid";
  const showFlatInputs = rateKind === "flat" || rateKind === "hybrid";

  const submit = async () => {
    if (accessToken === null) return;
    setSubmitting(true);
    setError(null);
    try {
      const payload: CommissionRuleCreatePayload = {
        scope_type: scopeType,
        // The backend's shape validator requires "0" on whichever side
        // the current rate_kind hides (percentage -> flats zeroed,
        // flat -> rates zeroed, hybrid -> both used).
        driver_rate: showRateInputs ? driverRate.trim() || "0" : "0",
        shipper_rate: showRateInputs ? shipperRate.trim() || "0" : "0",
        rate_kind: rateKind,
        driver_flat_inr: showFlatInputs ? driverFlatInr.trim() || "0" : "0",
        shipper_flat_inr: showFlatInputs ? shipperFlatInr.trim() || "0" : "0",
      };
      if (scopeType === "user" || scopeType === "load") {
        if (scopeId.trim().length === 0) {
          setError(`scope_id is required for scope_type='${scopeType}'.`);
          setSubmitting(false);
          return;
        }
        payload.scope_id = scopeId.trim();
      }
      if (scopeType === "vehicle_type") {
        if (scopeValue.trim().length === 0) {
          setError("scope_value is required (e.g. 'open_body').");
          setSubmitting(false);
          return;
        }
        payload.scope_value = scopeValue.trim();
      }
      if (scopeType === "pricing_model") {
        if (scopeValue.trim().length === 0) {
          setError("scope_value is required (e.g. 'fixed').");
          setSubmitting(false);
          return;
        }
        // Lowercased to match `loads.pricing_model`, which stores the
        // seeded catalog codes in lower case. A rule saved as "Fixed"
        // would simply never match and nothing would say so.
        payload.scope_value = scopeValue.trim().toLowerCase();
      }
      if (scopeType === "route") {
        if (scopeValue.trim().length === 0) {
          setError("scope_value is required (e.g. 'chennai->coimbatore').");
          setSubmitting(false);
          return;
        }
        // Lowercased here to match `services/commission.route_key`, which
        // lowercases the load's districts before looking a rule up. A rule
        // saved as "Chennai->Coimbatore" would simply never match, and
        // nothing would report that it had not.
        payload.scope_value = scopeValue.trim().toLowerCase();
      }
      if (notes.trim().length > 0) {
        payload.notes = notes.trim();
      }
      await commissionAdminApi.create(accessToken, payload);
      onCreated();
    } catch (e: unknown) {
      const msg = e instanceof ApiError ? e.problem.detail : "Unexpected error.";
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>New commission rule</CardTitle>
        <CardDescription>
          Pick a scope, set the driver + shipper rates as decimal fractions
          (0.02 = 2%).  The backend resolver picks the most-specific match.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="space-y-1">
          <Label htmlFor="rule-scope-type">Scope type</Label>
          <select
            id="rule-scope-type"
            className="block w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            value={scopeType}
            onChange={(e) => {
              setScopeType(e.target.value as CommissionScopeType);
              setScopeId("");
              setScopeUser(null);
              setScopeValue("");
            }}
            disabled={submitting}
          >
            {SCOPE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        {scopeType === "user" && (
          <div className="space-y-1">
            <Label htmlFor="rule-scope-user">User (driver or shipper)</Label>
            <UserPicker
              id="rule-scope-user"
              value={scopeUser}
              onChange={(u) => {
                setScopeUser(u);
                setScopeId(u?.id ?? "");
              }}
              placeholder="Search by phone or name…"
              disabled={submitting}
            />
            <p className="text-xs text-muted-foreground">
              Search hits the existing /admin/users endpoint; the rule
              attaches to the selected user's UUID.
            </p>
          </div>
        )}

        {scopeType === "load" && (
          <div className="space-y-1">
            <Label htmlFor="rule-scope-id">Load UUID</Label>
            <Input
              id="rule-scope-id"
              value={scopeId}
              onChange={(e) => {
                setScopeId(e.target.value);
              }}
              placeholder="00000000-0000-0000-0000-000000000000"
              disabled={submitting}
            />
            <p className="text-xs text-muted-foreground">
              Load picker isn't wired yet — paste the UUID from the load
              detail URL for now.
            </p>
          </div>
        )}

        {scopeType === "pricing_model" && (
          <div className="space-y-1">
            <Label htmlFor="rule-pricing-model">Pricing model</Label>
            <select
              id="rule-pricing-model"
              className="h-9 w-full rounded-md border bg-background px-3 text-sm"
              value={scopeValue}
              onChange={(e) => {
                setScopeValue(e.target.value);
              }}
              disabled={submitting}
            >
              <option value="">Select…</option>
              {PRICING_MODEL_CODES.map((code) => (
                <option key={code} value={code}>
                  {code}
                </option>
              ))}
            </select>
            <p className="text-xs text-muted-foreground">
              Applies to every load priced this way, whatever the lane or
              vehicle. Ranked below both of those, so a route-specific or
              vehicle-specific rate still wins.
            </p>
          </div>
        )}

        {scopeType === "route" && (
          <div className="space-y-1">
            <Label htmlFor="rule-route-value">Lane</Label>
            <Input
              id="rule-route-value"
              list="commission-route-lanes"
              value={scopeValue}
              onChange={(e) => {
                setScopeValue(e.target.value);
              }}
              placeholder="chennai->coimbatore"
              disabled={submitting}
            />
            {/* Suggestions come from the route master (migration 0109), so
                an operator picks a real lane instead of guessing the
                format. Still a free-text field: the resolver matches on
                the district pair, and lanes that are not in the master are
                perfectly valid to price. */}
            <datalist id="commission-route-lanes">
              {lanes.map((lane) => (
                <option key={lane} value={lane} />
              ))}
            </datalist>
            <p className="text-xs text-muted-foreground">
              Directional and lowercased: <code>chennai-&gt;coimbatore</code>{" "}
              is a different rule from the return leg, because a truck
              heading home empty is a different commercial proposition.
            </p>
          </div>
        )}

        {scopeType === "vehicle_type" && (
          <div className="space-y-1">
            <Label htmlFor="rule-scope-value">Vehicle type wire value</Label>
            <Input
              id="rule-scope-value"
              value={scopeValue}
              onChange={(e) => {
                setScopeValue(e.target.value);
              }}
              placeholder="open_body"
              disabled={submitting}
            />
            <p className="text-xs text-muted-foreground">
              Match the backend wire string exactly (e.g. open_body,
              closed_body, refrigerated, lcv_closed).
            </p>
          </div>
        )}

        <div className="space-y-1">
          <Label htmlFor="rule-rate-kind">Commission type</Label>
          <select
            id="rule-rate-kind"
            className="block w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            value={rateKind}
            onChange={(e) => {
              setRateKind(e.target.value as CommissionRateKind);
            }}
            disabled={submitting}
          >
            {RATE_KIND_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        {showRateInputs && (
          <div className="grid gap-3 md:grid-cols-2">
            <div className="space-y-1">
              <Label htmlFor="rule-driver-rate">Driver rate (0–1)</Label>
              <Input
                id="rule-driver-rate"
                value={driverRate}
                onChange={(e) => {
                  setDriverRate(e.target.value);
                }}
                placeholder="0.02"
                disabled={submitting}
              />
              <p className="text-xs text-muted-foreground">
                {formatRate(driverRate)} of the trip value collected from
                the driver.
              </p>
            </div>
            <div className="space-y-1">
              <Label htmlFor="rule-shipper-rate">Shipper rate (0–1)</Label>
              <Input
                id="rule-shipper-rate"
                value={shipperRate}
                onChange={(e) => {
                  setShipperRate(e.target.value);
                }}
                placeholder="0.02"
                disabled={submitting}
              />
              <p className="text-xs text-muted-foreground">
                {formatRate(shipperRate)} of the trip value collected from
                the shipper.
              </p>
            </div>
          </div>
        )}

        {showFlatInputs && (
          <div className="grid gap-3 md:grid-cols-2">
            <div className="space-y-1">
              <Label htmlFor="rule-driver-flat">Driver flat (₹)</Label>
              <Input
                id="rule-driver-flat"
                value={driverFlatInr}
                onChange={(e) => {
                  setDriverFlatInr(e.target.value);
                }}
                placeholder="200"
                disabled={submitting}
              />
              <p className="text-xs text-muted-foreground">
                {formatInr(driverFlatInr)} flat, charged to the driver per
                trip.
              </p>
            </div>
            <div className="space-y-1">
              <Label htmlFor="rule-shipper-flat">Shipper flat (₹)</Label>
              <Input
                id="rule-shipper-flat"
                value={shipperFlatInr}
                onChange={(e) => {
                  setShipperFlatInr(e.target.value);
                }}
                placeholder="200"
                disabled={submitting}
              />
              <p className="text-xs text-muted-foreground">
                {formatInr(shipperFlatInr)} flat, charged to the shipper
                per trip.
              </p>
            </div>
          </div>
        )}

        <div className="space-y-1">
          <Label htmlFor="rule-notes">Notes (admin-only)</Label>
          <Textarea
            id="rule-notes"
            value={notes}
            onChange={(e) => {
              setNotes(e.target.value);
            }}
            maxLength={500}
            placeholder="Why this rule exists.  E.g. 'Negotiated rate for ABC Logistics'."
            disabled={submitting}
          />
        </div>

        {error !== null && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onCancel} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={() => void submit()} disabled={submitting}>
            {submitting ? (
              <>
                <Loader2 className="mr-1 h-4 w-4 animate-spin" /> Creating…
              </>
            ) : (
              "Create rule"
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export function CommissionRulesPage(): React.JSX.Element {
  const { accessToken } = useAuth();
  const [rules, setRules] = useState<CommissionRule[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState<boolean>(false);
  const [deactivatingId, setDeactivatingId] = useState<string | null>(null);

  const load = useCallback(
    async (signal?: AbortSignal) => {
      if (accessToken === null) return;
      setLoading(true);
      setError(null);
      try {
        const res = await commissionAdminApi.list(accessToken, signal);
        setRules(res.rules);
      } catch (e: unknown) {
        if (e instanceof Error && e.name === "AbortError") return;
        const msg = e instanceof ApiError ? e.problem.detail : "Unexpected error.";
        setError(msg);
      } finally {
        setLoading(false);
      }
    },
    [accessToken],
  );

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => {
      controller.abort();
    };
  }, [load]);


  const deactivate = async (ruleId: string) => {
    if (accessToken === null) return;
    // AD-10: a reason is mandatory and is recorded in
    // audit.commission_rule_changes. Prompting instead of confirming,
    // because this changes what everyone matching the rule is charged
    // and "who stopped this rate, and why" is the question a pricing
    // dispute opens with. The server enforces 10-300 chars, so a short
    // or empty answer is rejected here rather than round-tripped.
    const reason = globalThis.prompt(
      "Why is this rule being deactivated? It will stop matching new "
      + "trips immediately, and the reason is recorded in the pricing "
      + "audit (10-300 characters).",
    );
    if (reason === null) return;
    if (reason.trim().length < 10) {
      setError(
        "A reason of at least 10 characters is required to deactivate a "
        + "commission rule.",
      );
      return;
    }
    setDeactivatingId(ruleId);
    try {
      await commissionAdminApi.deactivate(accessToken, ruleId, reason.trim());
      await load();
    } catch (e: unknown) {
      const msg = e instanceof ApiError ? e.problem.detail : "Unexpected error.";
      setError(msg);
    } finally {
      setDeactivatingId(null);
    }
  };

  return (
    <div className="space-y-4">
      <PageHeader
        title="Commission rules"
        description="Per-user / per-load / per-vehicle-type overrides on the 2 % + 2 % default rate."
        actions={
          !creating ? (
            <Button onClick={() => setCreating(true)}>
              <Plus className="mr-1 h-4 w-4" /> New rule
            </Button>
          ) : undefined
        }
      />
        {error !== null && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {creating && (
          <CreateRuleForm
            onCreated={() => {
              setCreating(false);
              void load();
            }}
            onCancel={() => {
              setCreating(false);
            }}
          />
        )}

        <Card>
          <CardHeader>
            <CardTitle>Active rules ({rules.length})</CardTitle>
            <CardDescription>
              The resolver picks the most-specific match per trip
              (load &gt; user &gt; vehicle_type &gt; global).
              Deactivation is soft — the row stays on disk for audit.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading && rules.length === 0 ? (
              <div className="flex items-center gap-2 py-8 text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading…
              </div>
            ) : rules.length === 0 ? (
              <div className="py-10 text-center text-muted-foreground">
                No active rules yet. The backend uses its 2 % + 2 % default.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                    <tr>
                      <th className="py-2 pr-4">Scope</th>
                      <th className="py-2 pr-4">Target</th>
                      <th className="py-2 pr-4">Type</th>
                      <th className="py-2 pr-4">Driver</th>
                      <th className="py-2 pr-4">Shipper</th>
                      <th className="py-2 pr-4">Effective from</th>
                      <th className="py-2 pr-4">Notes</th>
                      <th className="py-2 pr-4"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {rules.map((r) => (
                      <tr
                        key={r.id}
                        className="border-b align-top last:border-b-0"
                      >
                        <td className="py-3 pr-4">
                          <span
                            className={cn(
                              "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold",
                              SCOPE_PILL[r.scope_type],
                            )}
                          >
                            {r.scope_type}
                          </span>
                        </td>
                        <td className="py-3 pr-4">
                          {r.scope_label !== null ? (
                            <div>
                              <div className="font-medium">{r.scope_label}</div>
                              {r.scope_id !== null && (
                                <div className="text-xs text-muted-foreground font-mono">
                                  {shortId(r.scope_id)}
                                </div>
                              )}
                            </div>
                          ) : r.scope_id !== null ? (
                            <span className="font-mono text-xs">
                              {shortId(r.scope_id)}
                            </span>
                          ) : r.scope_value !== null ? (
                            <span>{r.scope_value}</span>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </td>
                        <td className="py-3 pr-4">{r.rate_kind}</td>
                        <td className="py-3 pr-4 tabular-nums">
                          <span className="inline-flex items-center gap-1">
                            <IndianRupee className="h-3 w-3" />
                            {r.rate_kind === "flat"
                              ? formatInr(r.driver_flat_inr)
                              : r.rate_kind === "hybrid"
                                ? `${formatRate(r.driver_rate)} + ${formatInr(r.driver_flat_inr)}`
                                : formatRate(r.driver_rate)}
                          </span>
                        </td>
                        <td className="py-3 pr-4 tabular-nums">
                          <span className="inline-flex items-center gap-1">
                            <IndianRupee className="h-3 w-3" />
                            {r.rate_kind === "flat"
                              ? formatInr(r.shipper_flat_inr)
                              : r.rate_kind === "hybrid"
                                ? `${formatRate(r.shipper_rate)} + ${formatInr(r.shipper_flat_inr)}`
                                : formatRate(r.shipper_rate)}
                          </span>
                        </td>
                        <td className="py-3 pr-4 text-xs text-muted-foreground">
                          {formatDate(r.effective_from)}
                        </td>
                        <td className="py-3 pr-4 text-xs">
                          {r.notes ?? "—"}
                        </td>
                        <td className="py-3 pr-4">
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={deactivatingId === r.id}
                            onClick={() => void deactivate(r.id)}
                          >
                            {deactivatingId === r.id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Trash2 className="h-4 w-4" />
                            )}
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
    </div>
  );
}
